import { useState } from "react";
import cx from "classnames";

import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorContainer from "client/common/components/errors/FetchErrorContainer";
import LoyaltyProgramMarketingBanners from "client/loyalty_programs/components/LoyaltyProgramMarketingBanners";
import { useCarouselDisplayQuery } from "client/products/hooks";
import CategoryCardV2 from "client/products/components/CategoryCardV2";
import { DEALS_CATEGORY } from "client/products/components/CategoryCard";
import {
  CategoryCarouselSection,
  TopSellerCarouselSection,
} from "client/products/components/CarouselSections";
import { partitionCarousels } from "client/products/utils";
import {
  BrowseAllPatientKind,
  CategoryCarousel,
  IBrowseFilterSpeciesKind,
} from "client/products/types";
import { Clinic } from "client/clinics/types";
import { useLoyaltyProgramBannersQuery } from "client/loyalty_programs/hooks";
import { ProductLineListSource } from "client/analytics/types";

import CatalogFilters from "./CatalogFilters";
import DealsMarketingBanner from "./DealsMarketingBanner";

import styles from "../styles/HomeBrowse.module.less";

type CategoryCarouselV2Props = {
  carousels: CategoryCarousel[];
};

export const CategoryCarouselV2 = ({ carousels }: CategoryCarouselV2Props) => {
  return (
    <HorizontalOverflow
      direction="right"
      classNames={{
        scroller: styles.categories,
      }}
    >
      <CategoryCardV2.Small category={DEALS_CATEGORY} index={0} />
      {carousels.map((c, i) => (
        <CategoryCardV2.Small key={c.category.id} category={c.category} index={i + 1} />
      ))}
    </HorizontalOverflow>
  );
};

type HomeBrowseContentProps = {
  species: IBrowseFilterSpeciesKind;
};

const HomeBrowseContent = ({ species }: HomeBrowseContentProps) => {
  const {
    data: display,
    isLoading: isBrowseLoading,
    error: browseError,
  } = useCarouselDisplayQuery({ species }, { enabled: true });
  const { data: loyaltyProgramBanners, isLoading: isLoyaltyProgramBannersLoading } =
    useLoyaltyProgramBannersQuery();

  if (isBrowseLoading || isLoyaltyProgramBannersLoading) {
    return <LoadingContainer />;
  }

  if (browseError || !display) {
    return <FetchErrorContainer error={browseError} entityName="categories" isPlural={true} />;
  }

  const { topSellerCarousel, categoryCarousels } = partitionCarousels(display.carousels);
  const shouldShowLoyaltyProgramBanners = loyaltyProgramBanners && loyaltyProgramBanners.length > 0;

  return (
    <div
      className={cx(
        styles.homeBrowseContent,
        shouldShowLoyaltyProgramBanners && styles.loyaltyProgramBanners,
      )}
    >
      <CategoryCarouselV2 carousels={categoryCarousels} />
      {shouldShowLoyaltyProgramBanners && (
        <LoyaltyProgramMarketingBanners banners={loyaltyProgramBanners} />
      )}
      {!loyaltyProgramBanners?.length && <DealsMarketingBanner />}
      <div className={styles.categorySections}>
        {topSellerCarousel && (
          <TopSellerCarouselSection
            productLines={topSellerCarousel.product_lines}
            eventViewName={ProductLineListSource.HomePageFeatured}
            classNames={styles}
          />
        )}
        {categoryCarousels.map((c, i) => (
          <CategoryCarouselSection
            key={c.category.id}
            category={c.category}
            index={i}
            productLines={c.product_lines}
            eventViewName={ProductLineListSource.HomePageCategory}
            classNames={styles}
          />
        ))}
      </div>
    </div>
  );
};

type HomeBrowseHeaderProps = {
  species: IBrowseFilterSpeciesKind;
  setSpecies: (species: IBrowseFilterSpeciesKind) => void;
  clinic: Clinic;
};

const HomeBrowseHeader = ({ species, setSpecies, clinic }: HomeBrowseHeaderProps) => {
  return (
    <div className={styles.heading}>
      <div className={styles.header}>
        Search by our <span className={styles.accent}>popular categories</span>
      </div>
      <div className={styles.actions}>
        <CatalogFilters species={species} setSpecies={setSpecies} clinic={clinic} />
      </div>
    </div>
  );
};

const HomeBrowseV2 = ({ clinic }: { clinic: Clinic }) => {
  const [species, setSpecies] = useState<IBrowseFilterSpeciesKind>(BrowseAllPatientKind.ALL);
  return (
    <div className={styles.homeBrowse}>
      <HomeBrowseHeader species={species} setSpecies={setSpecies} clinic={clinic} />
      <HomeBrowseContent species={species} />
    </div>
  );
};

export default HomeBrowseV2;
